// src/components/ProjectManagement/CreateProjectModal.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, addDoc, query, where } from 'firebase/firestore';
import { productCategories, getAllProducts, getProductById, loadCustomProducts, createCustomProduct } from '../../utils/productTemplates';
import WorkerAvailabilityCalendar from './WorkerAvailabilityCalendar';
import TaskAssignmentPanel from './TaskAssignmentPanel';
import CreateNewProductModal from './CreateNewProductModal';

const CreateProjectModal = ({ onClose, onCreate, currentUser }) => {
  const [clients, setClients] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Step 1: Project Details
  const [formData, setFormData] = useState({
    projectName: '',
    description: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    productType: 'custom'
  });
  const [projectNameError, setProjectNameError] = useState('');
  
  // Step 2: Client Info
  const [newClient, setNewClient] = useState({ name: '', phone: '', email: '', location: '' });
  const [isNewClient, setIsNewClient] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  
  // Step 3: Products & Tasks
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [customTaskInput, setCustomTaskInput] = useState({ name: '', description: '', days: 4, startDate: '', endDate: '' });
  const [showCustomTaskInput, setShowCustomTaskInput] = useState(false);
  const [saveToTemplate, setSaveToTemplate] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [customProducts, setCustomProducts] = useState([]);
  
  // Step 4: Task Assignment
  const [taskAssignments, setTaskAssignments] = useState({});
  const [allTasksAssigned, setAllTasksAssigned] = useState(false);

  // Fetch clients and workers
  useEffect(() => {
    const fetchData = async () => {
      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      setClients(clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      const workersSnapshot = await getDocs(collection(db, 'users'));
      const workersData = workersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setWorkers(workersData);
      
      // Load custom products from Firestore (ASYNC)
      const customProductsData = await loadCustomProducts();
      setCustomProducts(customProductsData);
    };
    fetchData();
  }, []);

  // Check for duplicate project name
  const checkDuplicateProject = async (name) => {
    const q = query(collection(db, 'projects'), where('projectName', '==', name));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  };

  // Add product to selection
  const addProduct = (product) => {
    if (!selectedProducts.find(p => p.id === product.id)) {
      const productTasks = product.defaultTasks.map(task => ({
        ...task,
        id: `${product.id}-${task.order}-${Date.now()}-${Math.random()}`,
        productId: product.id,
        productName: product.name,
        startDate: formData.startDate,
        endDate: calculateTaskEndDate(formData.startDate, task.defaultDays),
        days: task.defaultDays,
        description: task.description || ''
      }));
      setSelectedProducts([...selectedProducts, { ...product, tasks: productTasks }]);
      setTasks([...tasks, ...productTasks]);
    }
    setShowProductSelector(false);
  };

  // Calculate task end date based on start date and duration
  const calculateTaskEndDate = (startDate, days) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  // Add custom task to current project
  const addCustomTask = () => {
    if (!customTaskInput.name) {
      alert('Please enter a task name');
      return;
    }
    const newTask = {
      id: `custom-${Date.now()}-${Math.random()}`,
      name: customTaskInput.name,
      description: customTaskInput.description,
      days: customTaskInput.days,
      startDate: customTaskInput.startDate || formData.startDate,
      endDate: customTaskInput.endDate || calculateTaskEndDate(formData.startDate, customTaskInput.days),
      required: false,
      isCustom: true,
      order: tasks.length + 1
    };
    setTasks([...tasks, newTask]);
    setCustomTaskInput({ name: '', description: '', days: 4, startDate: '', endDate: '' });
    setShowCustomTaskInput(false);
    
    if (saveToTemplate && newProductName) {
      // Save to Firestore as new product template
      const newProduct = {
        name: newProductName,
        category: 'custom',
        defaultTasks: [{ 
          name: newTask.name, 
          defaultDays: newTask.days, 
          required: false, 
          order: 1,
          description: newTask.description
        }],
        createdAt: new Date()
      };
      createCustomProduct(newProductName, 'custom', [{
        name: newTask.name,
        days: newTask.days,
        required: false,
        description: newTask.description
      }]).then(async () => {
        const updatedProducts = await loadCustomProducts();
        setCustomProducts(updatedProducts);
        alert(`✅ Task saved to new product "${newProductName}"!`);
      });
    }
    setSaveToTemplate(false);
    setNewProductName('');
  };

  // Remove task from project
  const removeTask = (taskId) => {
    setTasks(tasks.filter(t => t.id !== taskId));
  };

  // Update task dates
  const updateTaskDates = (taskId, field, value) => {
    setTasks(tasks.map(t => t.id === taskId ? { ...t, [field]: value } : t));
  };

  // Handle task assignment (supports multiple workers)
  const handleAssignTask = (taskId, workerIds, task) => {
    const workerIdsArray = Array.isArray(workerIds) ? workerIds : [workerIds];
    setTaskAssignments({ ...taskAssignments, [taskId]: workerIdsArray });
  };

  // Check if all tasks are assigned
  useEffect(() => {
    setAllTasksAssigned(tasks.length > 0 && Object.keys(taskAssignments).length === tasks.length);
  }, [tasks, taskAssignments]);

  // Handle product created from modal
  const handleProductCreated = async (newProduct) => {
    const updatedProducts = await loadCustomProducts();
    setCustomProducts(updatedProducts);
    setSelectedCategory('custom');
    addProduct(newProduct);
    setShowCreateProductModal(false);
  };

  // Submit project
  const handleSubmit = async () => {
    if (step === 1) {
      if (!formData.projectName || !formData.endDate) {
        alert('Please fill in project name and end date');
        return;
      }
      const isDuplicate = await checkDuplicateProject(formData.projectName);
      if (isDuplicate) {
        setProjectNameError('A project with this name already exists. Please use a different name.');
        return;
      }
      setStep(2);
      return;
    }
    
    if (step === 2) {
      if (isNewClient && !newClient.name) {
        alert('Please enter client name');
        return;
      }
      setStep(3);
      return;
    }
    
    if (step === 3) {
      if (tasks.length === 0) {
        alert('Please add at least one task for this project');
        return;
      }
      setStep(4);
      return;
    }
    
    if (step === 4 && !allTasksAssigned) {
      alert('Please assign all tasks to workers before creating the project');
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    
    try {
      // Create or get client
      let clientId = selectedClientId;
      if (isNewClient && newClient.name) {
        const clientRef = await addDoc(collection(db, 'clients'), { ...newClient, createdAt: new Date() });
        clientId = clientRef.id;
      }

      // Create project
      const projectRef = await addDoc(collection(db, 'projects'), {
        projectName: formData.projectName,
        clientId: clientId || null,
        description: formData.description,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        productType: formData.productType,
        status: 'active',
        progress: 0,
        totalTasks: tasks.length,
        completedTasks: 0,
        createdBy: currentUser.uid,
        createdAt: new Date()
      });

      // Create tasks with assignments (supports multiple workers)
      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];
        const assignedWorkerIds = taskAssignments[task.id] || [];
        const assignedWorkerNames = assignedWorkerIds.map(id => {
          const worker = workers.find(w => w.id === id);
          return worker?.name || null;
        }).filter(Boolean);
        
        await addDoc(collection(db, 'tasks'), {
          projectId: projectRef.id,
          projectName: formData.projectName,
          title: task.name,
          description: task.description || '',
          category: task.productName || 'Custom',
          estimatedDays: task.days,
          startDate: new Date(task.startDate),
          endDate: new Date(task.endDate),
          status: 'pending',
          assignedTo: assignedWorkerIds,
          assignedToNames: assignedWorkerNames,
          order: i,
          createdAt: new Date()
        });
      }

      alert(`✅ Project "${formData.projectName}" created with ${tasks.length} tasks!`);
      onCreate(formData);
      onClose();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to create project');
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const stopPropagation = (e) => e.stopPropagation();

  // STEP 1: Project Details
  if (step === 1) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" style={{ maxWidth: '550px' }} onClick={stopPropagation}>
          <h2>Create New Project</h2>
          <div className="step-indicator">
            <div className="step active">1. Details</div>
            <div className="step">2. Client</div>
            <div className="step">3. Products & Tasks</div>
            <div className="step">4. Assign Tasks</div>
          </div>

          <label>Project Name *</label>
          <input 
            type="text" 
            value={formData.projectName} 
            onChange={(e) => {
              setFormData({...formData, projectName: e.target.value});
              setProjectNameError('');
            }} 
            placeholder="e.g., Executive Bed for Mr. Mukasa"
          />
          {projectNameError && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '5px' }}>{projectNameError}</div>}

          <label>Product Type</label>
          <select value={formData.productType} onChange={(e) => setFormData({...formData, productType: e.target.value})}>
            <option value="custom">Custom Furniture</option>
            <option value="bed">Bed</option>
            <option value="chair">Chair</option>
            <option value="table">Table</option>
            <option value="cabinet">Cabinet</option>
            <option value="sofa">Sofa</option>
            <option value="wardrobe">Wardrobe</option>
          </select>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div><label>Start Date</label><input type="date" value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} /></div>
            <div><label>End Date *</label><input type="date" value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} /></div>
          </div>

          <label>Description</label>
          <textarea rows="3" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />

          <div className="modal-actions">
            <button className="cancel-btn" onClick={onClose}>Cancel</button>
            <button className="assign-btn" onClick={handleSubmit}>Next →</button>
          </div>
        </div>
      </div>
    );
  }

  // STEP 2: Client Info
  if (step === 2) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" style={{ maxWidth: '550px' }} onClick={stopPropagation}>
          <h2>Client Information</h2>
          <div className="step-indicator">
            <div className="step completed">✓ 1. Details</div>
            <div className="step active">2. Client</div>
            <div className="step">3. Products & Tasks</div>
            <div className="step">4. Assign Tasks</div>
          </div>

          <label>Select Client</label>
          <select value={isNewClient ? 'new' : selectedClientId} onChange={(e) => {
            if (e.target.value === 'new') {
              setIsNewClient(true);
              setSelectedClientId('');
            } else {
              setIsNewClient(false);
              setSelectedClientId(e.target.value);
            }
          }}>
            <option value="">-- Select Client --</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name} - {c.phone}</option>)}
            <option value="new">+ Add New Client</option>
          </select>

          {isNewClient && (
            <div style={{ marginTop: '15px' }}>
              <input type="text" placeholder="Client Name" value={newClient.name} onChange={(e) => setNewClient({...newClient, name: e.target.value})} style={{ marginBottom: '8px', width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
              <input type="tel" placeholder="Phone" value={newClient.phone} onChange={(e) => setNewClient({...newClient, phone: e.target.value})} style={{ marginBottom: '8px', width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
              <input type="email" placeholder="Email" value={newClient.email} onChange={(e) => setNewClient({...newClient, email: e.target.value})} style={{ marginBottom: '8px', width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
              <input type="text" placeholder="Location" value={newClient.location} onChange={(e) => setNewClient({...newClient, location: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
            </div>
          )}

          <div className="modal-actions">
            <button className="cancel-btn" onClick={() => setStep(1)}>← Back</button>
            <button className="assign-btn" onClick={handleSubmit}>Next →</button>
          </div>
        </div>
      </div>
    );
  }

  // STEP 3: Products & Tasks
  if (step === 3) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" style={{ maxWidth: '1000px', maxHeight: '85vh', overflowY: 'auto' }} onClick={stopPropagation}>
          <h2>Products & Tasks</h2>
          <div className="step-indicator">
            <div className="step completed">✓ 1. Details</div>
            <div className="step completed">✓ 2. Client</div>
            <div className="step active">3. Products & Tasks</div>
            <div className="step">4. Assign Tasks</div>
          </div>

          <div className="project-timeline-banner">
            <span>📅 Project Timeline: {formData.startDate} → {formData.endDate}</span>
          </div>

          <div className="add-product-section">
            <button className="add-product-btn" onClick={() => setShowProductSelector(!showProductSelector)}>+ Add Product</button>
            <button className="add-custom-task-btn" onClick={() => setShowCustomTaskInput(!showCustomTaskInput)}>+ Add Custom Task</button>
          </div>

          {showProductSelector && (
            <div className="product-selector-dropdown">
              <div className="product-categories">
                {Object.values(productCategories).map(category => (
                  <div key={category.id} className="product-category">
                    <div className="category-header" onClick={() => setSelectedCategory(selectedCategory === category.id ? null : category.id)}>
                      <span>{category.icon}</span><span>{category.name}</span>
                    </div>
                    {selectedCategory === category.id && (
                      <div className="category-products">
                        {category.products.map(product => (
                          <div key={product.id} className="product-item" onClick={() => addProduct(product)}>{product.name}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div className="product-category">
                  <div className="category-header" onClick={() => setSelectedCategory(selectedCategory === 'custom' ? null : 'custom')}>
                    <span>✨</span><span>Custom Products</span>
                  </div>
                  {selectedCategory === 'custom' && (
                    <div className="category-products">
                      {customProducts.length > 0 ? customProducts.map(product => (
                        <div key={product.id} className="product-item" onClick={() => addProduct(product)}>{product.name}</div>
                      )) : (
                        <div className="product-item disabled">No custom products yet</div>
                      )}
                      <div className="product-item create-new-item" onClick={() => setShowCreateProductModal(true)}>+ Create New Product</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {showCustomTaskInput && (
            <div className="custom-task-input-section">
              <h4>➕ Add Custom Task</h4>
              <input type="text" placeholder="Task name" value={customTaskInput.name} onChange={(e) => setCustomTaskInput({...customTaskInput, name: e.target.value})} />
              <textarea placeholder="Description" rows="2" value={customTaskInput.description} onChange={(e) => setCustomTaskInput({...customTaskInput, description: e.target.value})} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <div><label>Duration (days)</label><input type="number" value={customTaskInput.days} onChange={(e) => setCustomTaskInput({...customTaskInput, days: parseInt(e.target.value)})} min="1" /></div>
                <div><label>Start Date</label><input type="date" value={customTaskInput.startDate} onChange={(e) => setCustomTaskInput({...customTaskInput, startDate: e.target.value})} /></div>
                <div><label>End Date</label><input type="date" value={customTaskInput.endDate} onChange={(e) => setCustomTaskInput({...customTaskInput, endDate: e.target.value})} /></div>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input type="checkbox" checked={saveToTemplate} onChange={(e) => setSaveToTemplate(e.target.checked)} />
                  Save as new product template
                </label>
                {saveToTemplate && (
                  <input type="text" placeholder="Product name" value={newProductName} onChange={(e) => setNewProductName(e.target.value)} style={{ flex: 1, minWidth: '150px' }} />
                )}
              </div>
              <div className="custom-task-actions">
                <button className="save-btn" onClick={addCustomTask}>Add Task</button>
                <button className="cancel-btn" onClick={() => setShowCustomTaskInput(false)}>Cancel</button>
              </div>
            </div>
          )}

          <div className="tasks-list-section">
            <h3>📋 Project Tasks ({tasks.length})</h3>
            {tasks.length === 0 ? (
              <div className="empty-tasks">No tasks added yet. Add products or custom tasks above.</div>
            ) : (
              <div className="tasks-grid">
                {tasks.map(task => (
                  <div key={task.id} className="task-item-card">
                    <div className="task-header">
                      <strong>{task.name}</strong>
                      {task.productName && <span className="product-badge">{task.productName}</span>}
                      <button className="remove-task-btn" onClick={() => removeTask(task.id)}>✕</button>
                    </div>
                    {task.description && <p className="task-desc">{task.description}</p>}
                    <div className="task-dates">
                      <div><label>Start Date</label><input type="date" value={task.startDate} onChange={(e) => updateTaskDates(task.id, 'startDate', e.target.value)} /></div>
                      <div><label>End Date</label><input type="date" value={task.endDate} onChange={(e) => updateTaskDates(task.id, 'endDate', e.target.value)} /></div>
                      <div><label>Duration</label><input type="number" value={task.days} onChange={(e) => updateTaskDates(task.id, 'days', parseInt(e.target.value))} min="1" /> days</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button className="cancel-btn" onClick={() => setStep(2)}>← Back</button>
            <button className="assign-btn" onClick={handleSubmit} disabled={tasks.length === 0}>Next: Assign Tasks →</button>
          </div>

          {showCreateProductModal && (
            <CreateNewProductModal 
              onClose={() => setShowCreateProductModal(false)} 
              onProductCreated={handleProductCreated}
            />
          )}
        </div>
      </div>
    );
  }

  // STEP 4: Assign Tasks to Workers
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '1200px', maxHeight: '85vh', overflowY: 'auto' }} onClick={stopPropagation}>
        <h2>Assign Tasks to Workers</h2>
        <div className="step-indicator">
          <div className="step completed">✓ 1. Details</div>
          <div className="step completed">✓ 2. Client</div>
          <div className="step completed">✓ 3. Products & Tasks</div>
          <div className="step active">4. Assign Tasks</div>
        </div>
{/* 
        <div className="worker-availability-section">
          <h3>👥 Worker Availability</h3>
          <WorkerAvailabilityCalendar 
            workers={workers}
            tasks={[]}
            selectedDateRange={{ start: new Date(formData.startDate), end: new Date(formData.endDate) }}
            onSelectWorker={() => {}}
            selectedWorkerId={null}
          />
        </div> */}

        <TaskAssignmentPanel 
          tasks={tasks}
          workers={workers}
          onAssignTask={handleAssignTask}
          onAssignAll={() => {}}
          projectStartDate={formData.startDate}
          projectEndDate={formData.endDate}
        />

        <div className="modal-actions">
          <button className="cancel-btn" onClick={() => setStep(3)}>← Back</button>
          <button className="assign-btn" onClick={handleSubmit} disabled={!allTasksAssigned || loading}>
            {loading ? 'Creating...' : `✓ Create Project (${tasks.length} tasks)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;